<template>
  <div class="insights-page">

    <!-- HERO -->
    <section class="ins-hero">
      <div class="container">
        <div class="ins-hero__eyebrow">
          <span class="ins-hero__badge"><i class="fa fa-magic"></i> {{ hero_badge }}</span>
          <span class="ins-hero__sub">{{ hero_sub }}</span>
        </div>
        <h1 class="ins-hero__title">{{ heroTitleBefore }}<span class="ins-hero__accent">{{ hero_title_accent }}</span>{{ heroTitleAfter }}</h1>
        <p class="ins-hero__desc">{{ hero_desc }}</p>
      </div>
    </section>

    <!-- FEATURED -->
    <section v-if="featuredPost && !loading" class="ins-featured">
      <div class="container">
        <div class="ins-featured__eyebrow">{{ featured_eyebrow }}</div>
        <div class="ins-featured__card" @click="goTo(featuredPost)">
          <div class="ins-featured__grid">
            <div class="ins-featured__left">
              <div class="ins-featured__meta-top">
                <span class="ins-kind-badge"><i :class="kindIcon(featuredPost.post_type)"></i> {{ kindLabel(featuredPost.post_type) }}</span>
                <span class="ins-featured__topic">{{ featuredPost.post_type.replace('_', ' ') }}</span>
              </div>
              <h2 class="ins-featured__title">{{ featuredPost.title }}</h2>
              <p class="ins-featured__dek">{{ featuredPost.excerpt || stripHtml(featuredPost.content) }}</p>
              <div class="ins-featured__meta">
                <span v-if="featuredPost.author" class="ins-featured__author">{{ featuredPost.author }}</span>
                <span v-if="featuredPost.author">·</span>
                <span>{{ formatDate(featuredPost.created_at) }}</span>
                <span v-if="featuredPost.read_time">·</span>
                <span v-if="featuredPost.read_time" class="ins-meta-time"><i class="fa fa-clock-o"></i> {{ featuredPost.read_time }}</span>
              </div>
            </div>
            <div class="ins-featured__right">
              <span class="ins-featured__cta">{{ featured_cta }} <i class="fa fa-arrow-right"></i></span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- LIBRARY -->
    <section class="ins-library">
      <div class="container">
        <div class="ins-library__header">
          <div>
            <div class="ins-library__eyebrow">{{ library_eyebrow }}</div>
            <h2 class="ins-library__title">{{ library_title }}</h2>
          </div>
          <div class="ins-filters">
            <button v-for="tab in tabs" :key="tab.value"
              class="ins-filter-btn" :class="{ active: activeTab === tab.value }"
              @click="setTab(tab.value)">{{ tab.label }}</button>
          </div>
        </div>

        <div v-if="loading" class="ins-loading"><spinner></spinner></div>
        <div v-else-if="gridPosts.length === 0" class="ins-empty">No insights found.</div>
        <div v-else class="ins-grid">
          <div v-for="post in gridPosts" :key="post.id" class="ins-card" @click="goTo(post)">
            <div class="ins-card__top">
              <div class="ins-card__icon"><i :class="kindIcon(post.post_type)"></i></div>
              <span class="ins-card__kind">{{ kindLabel(post.post_type) }}</span>
            </div>
            <h3 class="ins-card__title">{{ post.title }}</h3>
            <p class="ins-card__dek">{{ post.excerpt || stripHtml(post.content) }}</p>
            <div class="ins-card__footer">
              <span class="ins-card__author">{{ post.author || 'SherlockIT Team' }}</span>
              <span class="ins-card__time"><i class="fa fa-clock-o"></i> {{ post.read_time || '5 min' }}</span>
            </div>
          </div>
        </div>

        <div v-if="blog.next && !loading" class="ins-loadmore">
          <button class="ins-loadmore__btn" @click="loadMore">Load more articles</button>
        </div>
      </div>
    </section>

    <!-- NEWSLETTER -->
    <section class="ins-newsletter">
      <div class="container">
        <div class="ins-newsletter__inner">
          <div class="ins-newsletter__copy">
            <div class="ins-newsletter__eyebrow">{{ newsletter_eyebrow }}</div>
            <h2 class="ins-newsletter__title">{{ newsletter_title }}</h2>
            <p class="ins-newsletter__desc">{{ newsletter_desc }}</p>
          </div>
          <form class="ins-newsletter__form" @submit.prevent>
            <input type="email" :placeholder="newsletter_placeholder" class="ins-newsletter__input" />
            <button type="submit" class="ins-newsletter__btn">{{ newsletter_btn }}</button>
          </form>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
import Moment from "moment";
import Spinner from "@/components/Spinner";
import { cmsHome } from "@/services/cms";
import axios from "axios";
import store from "@/store";

const fetchCmsInsights = (defaults) => {
  const base = store.getters.baseUrl.replace(/\/$/, "");
  const token = new URLSearchParams(window.location.search).get("token");
  const url = token
    ? `${base}/cms-api/preview/?token=${encodeURIComponent(token)}`
    : `${base}/cms-api/page/insights/`;
  return axios.get(url).then(r => ({ ...defaults, ...r.data })).catch(() => defaults);
};

export default {
  metaInfo() { return { title: "Insights — SherlockIT" }; },
  components: { Spinner },
  data() {
    return {
      // CMS text fields (overwritten by mounted)
      hero_badge: "Insights from the SherlockIT intelligence team",
      hero_sub: "Research · Case studies · Briefings",
      hero_title: "Field notes on",
      hero_title_accent: "verified corporate intelligence",
      hero_desc: "Long-form analysis on GraphRAG, AML typologies, sovereign AI, and the messy reality of 120+ jurisdictions — written by the team that builds the platform.",
      featured_eyebrow: "Featured research",
      featured_cta: "Read the report",
      library_eyebrow: "The library",
      library_title: "Everything we've published.",
      newsletter_eyebrow: "The Verified Brief",
      newsletter_title: "Audit-grade intelligence in your inbox.",
      newsletter_desc: "Monthly notes on corporate identity, AML typologies and GraphRAG patterns. No marketing — written by the team that builds the platform.",
      newsletter_placeholder: "you@company.com",
      newsletter_btn: "Subscribe",
      // Data
      blogs: [],
      blog: { count: null, next: null, previous: null },
      page: 1,
      loading: true,
      activeTab: "all",
      tabs: [
        { label: "All",        value: "all"       },
        { label: "Article",    value: "article"   },
        { label: "Research",   value: "research"  },
        { label: "Case study", value: "case_study"},
        { label: "Briefing",   value: "briefing"  },
      ],
      kindLabels: { article: "Article", research: "Research", case_study: "Case study", briefing: "Briefing" },
      kindIcons:  { article: "fa fa-file-text-o", research: "fa fa-book", case_study: "fa fa-rss", briefing: "fa fa-shield" },
    };
  },
  computed: {
    heroTitleBefore() {
      const idx = this.hero_title.indexOf(this.hero_title_accent);
      return idx >= 0 ? this.hero_title.slice(0, idx) : this.hero_title + ' ';
    },
    heroTitleAfter() {
      const idx = this.hero_title.indexOf(this.hero_title_accent);
      return idx >= 0 ? this.hero_title.slice(idx + this.hero_title_accent.length) : '';
    },
    filteredBlogs() {
      if (this.activeTab === "all") return this.blogs;
      return this.blogs.filter(b => b.post_type === this.activeTab);
    },
    featuredPost() {
      return this.blogs.find(b => b.is_featured) || null;
    },
    gridPosts() {
      const posts = this.filteredBlogs;
      if (this.activeTab === "all" && this.featuredPost) {
        return posts.filter(b => b.id !== this.featuredPost.id);
      }
      return posts;
    },
  },
  async mounted() {
    const cms = await fetchCmsInsights(this.$data);
    const fields = ['hero_badge','hero_sub','hero_title','hero_title_accent','hero_desc',
      'featured_eyebrow','featured_cta','library_eyebrow','library_title',
      'newsletter_eyebrow','newsletter_title','newsletter_desc','newsletter_placeholder','newsletter_btn'];
    fields.forEach(f => { if (cms[f]) this[f] = cms[f]; });
    this.updateBlogs();
  },
  methods: {
    updateBlogs() {
      this.loading = true;
      this.$axios.get("insights/posts/?page=" + this.page)
        .then(res => {
          this.blog.count = res.data.count;
          this.blog.next = res.data.next;
          this.blog.previous = res.data.previous;
          this.blogs.push(...res.data.results);
          this.loading = false;
        })
        .catch(() => { this.loading = false; });
    },
    loadMore() { this.page++; this.updateBlogs(); },
    setTab(val) { this.activeTab = val; },
    kindLabel(type) { return this.kindLabels[type] || type; },
    kindIcon(type)  { return this.kindIcons[type]  || "fa fa-file-o"; },
    formatDate(date) { return Moment(date).format("MMM D, YYYY"); },
    stripHtml(html) {
      if (!html) return "";
      const tmp = document.createElement("div");
      tmp.innerHTML = html;
      return (tmp.textContent || tmp.innerText || "").substring(0, 160) + "…";
    },
    goTo(post) { this.$router.push("/insights/" + post.id + "/" + post.slug); },
  },
};
</script>

<style scoped>
.insights-page { background: #fff; }
.container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }

/* Hero */
.ins-hero { border-bottom: 1px solid #e2e8f0; padding: 40px 0 56px; background: #fff; }
.ins-hero__eyebrow { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; margin-bottom: 32px; }
.ins-hero__badge { display: inline-flex; align-items: center; gap: 6px; background: rgba(87,94,207,0.08); color: #575ECF; border-radius: 100px; padding: 5px 12px; font-size: 12px; font-weight: 500; }
.ins-hero__sub { font-size: 12px; color: #94a3b8; }
.ins-hero__title { font-size: clamp(2rem, 5vw, 3.5rem); font-weight: 600; letter-spacing: -0.02em; color: #0a1428; line-height: 1.05; max-width: 860px; margin-bottom: 24px; }
.ins-hero__accent { color: #575ECF; }
.ins-hero__desc { font-size: 1.1rem; color: #64748b; max-width: 640px; line-height: 1.7; }

/* Featured */
.ins-featured { border-bottom: 1px solid #e2e8f0; background: #f8fafc; padding: 56px 0; }
.ins-featured__eyebrow { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; color: #575ECF; margin-bottom: 20px; }
.ins-featured__card { background: #fff; border: 1px solid #e2e8f0; border-radius: 16px; padding: 24px 32px; cursor: pointer; transition: border-color 0.2s, box-shadow 0.2s; }
.ins-featured__card:hover { border-color: rgba(87,94,207,0.4); box-shadow: 0 2px 16px rgba(87,94,207,0.08); }
.ins-featured__grid { display: grid; grid-template-columns: 1.4fr 1fr; gap: 32px; align-items: center; }
.ins-featured__meta-top { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; }
.ins-kind-badge { display: inline-flex; align-items: center; gap: 5px; background: rgba(87,94,207,0.1); color: #575ECF; border-radius: 100px; padding: 4px 10px; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; }
.ins-featured__topic { font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: #94a3b8; }
.ins-featured__title { font-size: 1.6rem; font-weight: 600; letter-spacing: -0.02em; color: #0a1428; line-height: 1.25; margin-bottom: 12px; transition: color 0.15s; }
.ins-featured__card:hover .ins-featured__title { color: #575ECF; }
.ins-featured__dek { font-size: 0.95rem; color: #64748b; line-height: 1.7; margin-bottom: 16px; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.ins-featured__meta { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; font-size: 12px; color: #94a3b8; }
.ins-featured__author { color: #475569; font-weight: 500; }
.ins-meta-time { display: inline-flex; align-items: center; gap: 4px; }
.ins-featured__right { display: flex; align-items: center; justify-content: flex-end; }
.ins-featured__cta { display: inline-flex; align-items: center; gap: 8px; background: #575ECF; color: #fff; border-radius: 10px; padding: 10px 20px; font-size: 13px; font-weight: 500; transition: gap 0.15s, background 0.15s; }
.ins-featured__card:hover .ins-featured__cta { background: #4750b8; gap: 12px; }

/* Library */
.ins-library { padding: 80px 0; background: #fff; }
.ins-library__header { display: flex; align-items: flex-end; justify-content: space-between; flex-wrap: wrap; gap: 20px; margin-bottom: 40px; }
.ins-library__eyebrow { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; color: #575ECF; margin-bottom: 8px; }
.ins-library__title { font-size: 2rem; font-weight: 600; letter-spacing: -0.02em; color: #0a1428; margin: 0; }
.ins-filters { display: flex; flex-wrap: wrap; gap: 6px; }
.ins-filter-btn { padding: 6px 12px; border-radius: 8px; border: 1px solid #e2e8f0; background: #f8fafc; color: #64748b; font-size: 12px; font-weight: 500; cursor: pointer; transition: all 0.15s; white-space: nowrap; }
.ins-filter-btn:hover { color: #0a1428; border-color: rgba(87,94,207,0.4); }
.ins-filter-btn.active { background: #575ECF; color: #fff; border-color: #575ECF; }

/* Loading / Empty */
.ins-loading { text-align: center; padding: 60px 0; }
.ins-empty { text-align: center; padding: 60px 0; color: #94a3b8; font-size: 1rem; }

/* Grid */
.ins-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 20px; }
.ins-card { display: flex; flex-direction: column; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 16px; padding: 24px; cursor: pointer; transition: border-color 0.2s, box-shadow 0.2s; }
.ins-card:hover { border-color: rgba(87,94,207,0.4); box-shadow: 0 2px 16px rgba(87,94,207,0.07); }
.ins-card__top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
.ins-card__icon { width: 40px; height: 40px; border-radius: 10px; background: rgba(87,94,207,0.1); color: #575ECF; display: flex; align-items: center; justify-content: center; font-size: 16px; flex-shrink: 0; }
.ins-card__kind { font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: #94a3b8; font-weight: 500; }
.ins-card__title { font-size: 0.95rem; font-weight: 600; letter-spacing: -0.01em; color: #0a1428; line-height: 1.45; margin-bottom: 8px; flex: 1; transition: color 0.15s; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.ins-card:hover .ins-card__title { color: #575ECF; }
.ins-card__dek { font-size: 0.85rem; color: #64748b; line-height: 1.65; margin-bottom: 20px; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.ins-card__footer { display: flex; align-items: center; justify-content: space-between; border-top: 1px solid #e2e8f0; padding-top: 12px; font-size: 11px; color: #94a3b8; margin-top: auto; }
.ins-card__author { color: #475569; font-weight: 500; }
.ins-card__time { display: inline-flex; align-items: center; gap: 4px; }

/* Load more */
.ins-loadmore { text-align: center; margin-top: 48px; }
.ins-loadmore__btn { padding: 10px 28px; border-radius: 8px; border: 1px solid #e2e8f0; background: #fff; font-size: 14px; font-weight: 500; color: #475569; cursor: pointer; transition: border-color 0.15s, color 0.15s; }
.ins-loadmore__btn:hover { border-color: #575ECF; color: #575ECF; }

/* Newsletter */
.ins-newsletter { border-top: 1px solid #e2e8f0; background: #0a1428; padding: 64px 0; }
.ins-newsletter__inner { display: grid; grid-template-columns: 1.3fr 1fr; gap: 48px; align-items: center; }
.ins-newsletter__eyebrow { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; color: #4ade80; margin-bottom: 12px; }
.ins-newsletter__title { font-size: 1.75rem; font-weight: 600; color: #fff; letter-spacing: -0.02em; margin-bottom: 12px; }
.ins-newsletter__desc { font-size: 0.9rem; color: rgba(255,255,255,0.65); line-height: 1.7; max-width: 460px; }
.ins-newsletter__form { display: flex; align-items: center; gap: 8px; }
.ins-newsletter__input { flex: 1; border: 1px solid rgba(255,255,255,0.15); background: rgba(255,255,255,0.05); border-radius: 8px; padding: 10px 16px; font-size: 14px; color: #fff; outline: none; transition: border-color 0.15s; }
.ins-newsletter__input::placeholder { color: rgba(255,255,255,0.35); }
.ins-newsletter__input:focus { border-color: rgba(255,255,255,0.4); }
.ins-newsletter__btn { border-radius: 8px; background: #fff; color: #0a1428; padding: 10px 20px; font-size: 14px; font-weight: 500; border: none; cursor: pointer; white-space: nowrap; transition: background 0.15s; }
.ins-newsletter__btn:hover { background: rgba(255,255,255,0.9); }

/* Responsive */
@media (max-width: 1024px) { .ins-grid { grid-template-columns: repeat(2,1fr); } }
@media (max-width: 768px) {
  .ins-featured__grid { grid-template-columns: 1fr; gap: 20px; }
  .ins-featured__right { justify-content: flex-start; }
  .ins-library__header { flex-direction: column; align-items: flex-start; }
  .ins-newsletter__inner { grid-template-columns: 1fr; gap: 32px; }
}
@media (max-width: 560px) { .ins-grid { grid-template-columns: 1fr; } }
</style>
